<script lang="ts" setup>
  import { ref, computed, onMounted, watch } from 'vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import MultiSelectField from '@/components/admin/form/MultiSelectField.vue'
  import SkeletonLoaderInlineField from '@/components/skeleton/SkeletonLoaderInlineField.vue'
  import type { Option as SelectOption } from '@/components/admin/form/SelectField.vue'
  import type { Option as MultiSelectOption } from '@/components/admin/form/MultiSelectField.vue'
  import {
    FormsService,
    QuestionnairesService,
    EmailTemplatesService,
    type TForm,
    type TQuestionnaire,
    type TEmailTemplate,
    type TAction
  } from '@/types/generated'
  import {
    type EmailAttachment,
    type EmailAttachmentType,
    type SendClientTemplateEmailPayload
  } from '@/types/agent-actions/sendClientTemplateEmail'
  import { useTranslation } from '@/composables/useTranslation'

  /** `TAction.payload` is generated as raw `string`; narrow it for this action. */
  type SendClientTemplateEmailAction = Omit<TAction, 'payload'> & {
    payload?: SendClientTemplateEmailPayload | null
  }

  // Translation setup
  const { t } = useTranslation({
    en: {
      selectTemplate: 'Select template...',
      selectQuestionnaires: 'Select questionnaires...',
      selectPdfForms: 'Select PDF forms...',
      noTemplatesFound: 'No templates found',
      noQuestionnairesFound: 'No questionnaires found',
      noPdfFormsFound: 'No PDF forms found',
      sendEmailTemplate: 'Send email template',
      toTheClient: 'to the client',
      // Editor (pending action) — present tense.
      withQuestionnaireSingular: 'with the questionnaire',
      withQuestionnairePlural: 'with the questionnaires',
      andPdfFormSingular: 'and the PDF form',
      andPdfFormPlural: 'and the PDF forms',
      // Summary (committed/reverted/errored action) — wording may diverge later.
      withQuestionnaireSingularCommitted: 'with the questionnaire',
      withQuestionnairesPluralCommitted: 'with the questionnaires',
      andPdfFormSingularCommitted: 'and the PDF form',
      andPdfFormsPluralCommitted: 'and the PDF forms',
      emailTemplateSent: 'The email template',
      wassentToClient: 'was sent to the client'
    },
    de: {
      selectTemplate: 'Vorlage auswählen...',
      selectQuestionnaires: 'Fragebögen auswählen...',
      selectPdfForms: 'PDF-Formulare auswählen...',
      noTemplatesFound: 'Keine Vorlagen gefunden',
      noQuestionnairesFound: 'Keine Fragebögen gefunden',
      noPdfFormsFound: 'Keine PDF-Formulare gefunden',
      sendEmailTemplate: 'Sende die E-Mail-Vorlage',
      toTheClient: 'an den Mandanten.',
      // Editor (pending action) — present tense.
      withQuestionnaireSingular: 'mit dem Fragebogen',
      withQuestionnairePlural: 'mit den Fragebögen',
      andPdfFormSingular: 'und dem PDF-Formular',
      andPdfFormPlural: 'und den PDF-Formularen',
      // Summary (committed/reverted/errored action) — wording may diverge later.
      withQuestionnaireSingularCommitted: 'mit dem Fragebogen',
      withQuestionnairesPluralCommitted: 'mit den Fragebögen',
      andPdfFormSingularCommitted: 'und dem PDF-Formular',
      andPdfFormsPluralCommitted: 'und den PDF-Formularen',
      emailTemplateSent: 'Die E-Mail-Vorlage',
      wassentToClient: 'wurde an den Mandanten gesendet.'
    }
  })

  // Define props
  interface Props {
    action: SendClientTemplateEmailAction
    customPayload?: SendClientTemplateEmailPayload | null
  }

  const props = defineProps<Props>()

  // Define emits to pass through to parent
  const emit = defineEmits<{
    payloadChanged: [payload: SendClientTemplateEmailPayload]
  }>()

  // Reactive state
  const templates = ref<TEmailTemplate[]>([])
  const forms = ref<TForm[]>([])
  const questionnaires = ref<TQuestionnaire[]>([])
  const isLoadingTemplates = ref(false)
  const isLoadingForms = ref(false)
  const isLoadingQuestionnaires = ref(false)
  const selectedTemplateUuid = ref<string>('')
  const selectedQuestionnaireUuids = ref<string[]>([])
  const selectedPdfFormUuids = ref<string[]>([])

  const templateOptions = computed<SelectOption[]>(() => {
    return templates.value.map((template) => ({
      value: template.uuid,
      title: template.name,
      icon: undefined
    }))
  })

  const questionnaireOptions = computed<MultiSelectOption[]>(() => {
    return questionnaires.value.map((questionnaire) => ({
      value: questionnaire.uuid,
      title: questionnaire.title,
      icon: undefined
    }))
  })

  const pdfFormOptions = computed<MultiSelectOption[]>(() => {
    return forms.value.map((form) => ({
      value: form.id, // Forms use id, not uuid
      title: form.title || 'Untitled Form',
      icon: undefined
    }))
  })

  // Computed properties for dynamic text generation
  const selectedTemplate = computed(() => {
    return templates.value.find((template) => template.uuid === selectedTemplateUuid.value)
  })

  const selectedQuestionnaires = computed(() => {
    return questionnaires.value.filter((questionnaire) =>
      selectedQuestionnaireUuids.value.includes(questionnaire.uuid)
    )
  })

  const selectedPdfForms = computed(() => {
    return forms.value.filter((form) => selectedPdfFormUuids.value.includes(form.id))
  })

  const hasQuestionnaires = computed(() => selectedQuestionnaireUuids.value.length > 0)
  const hasPdfForms = computed(() => selectedPdfFormUuids.value.length > 0)

  // Check if action should show summary (committed, reverted, or has error)
  const shouldShowSummary = computed(
    () =>
      props.action?.is_committed === true ||
      props.action?.is_reverted === true ||
      props.action?.has_commit_error === true ||
      (props.action?.is_commitable === false &&
        (selectedTemplateUuid.value || hasQuestionnaires.value || hasPdfForms.value))
  )

  // Summary text for committed/cancelled/error actions
  const summaryText = computed(() => {
    if (!shouldShowSummary.value || !selectedTemplate.value) return ''

    const templateName = selectedTemplate.value.name
    const questionnaireNames = selectedQuestionnaires.value.map((q) => q.title).join(', ')
    const pdfFormNames = selectedPdfForms.value.map((f) => f.title).join(', ')

    let summaryParts = []

    // Add main content
    summaryParts.push(t('emailTemplateSent'), `"${templateName}"`)

    // Add questionnaires if any
    if (selectedQuestionnaires.value.length > 0) {
      const questionnaireText =
        selectedQuestionnaires.value.length === 1
          ? t('withQuestionnaireSingularCommitted')
          : t('withQuestionnairesPluralCommitted')
      summaryParts.push(questionnaireText, `"${questionnaireNames}"`)
    }

    // Add PDF forms if any
    if (selectedPdfForms.value.length > 0) {
      const pdfFormText =
        selectedPdfForms.value.length === 1
          ? t('andPdfFormSingularCommitted')
          : t('andPdfFormsPluralCommitted')
      summaryParts.push(pdfFormText, `"${pdfFormNames}"`)
    }

    // Always end with standard text
    summaryParts.push(t('wassentToClient'))
    return summaryParts.join(' ')
  })

  // Methods
  const fetchTemplates = async () => {
    isLoadingTemplates.value = true
    try {
      const response = await EmailTemplatesService.emailTemplatesList({ limit: 100 })
      templates.value = response.results || []
    } catch (error) {
      console.error('Error fetching templates:', error)
      templates.value = []
    } finally {
      isLoadingTemplates.value = false
    }
  }

  const fetchForms = async () => {
    isLoadingForms.value = true
    try {
      const response = await FormsService.formsList({
        tag: 'MASTER',
        limit: 100
      })
      forms.value = response.results || []
    } catch (error) {
      console.error('Error fetching forms:', error)
      forms.value = []
    } finally {
      isLoadingForms.value = false
    }
  }

  const fetchQuestionnaires = async () => {
    isLoadingQuestionnaires.value = true
    try {
      const response = await QuestionnairesService.questionnairesList({ limit: 100 })
      questionnaires.value = response.results || []
    } catch (error) {
      console.error('Error fetching questionnaires:', error)
      questionnaires.value = []
    } finally {
      isLoadingQuestionnaires.value = false
    }
  }

  const handleTemplateSelection = (templateUuid: string) => {
    selectedTemplateUuid.value = templateUuid
    emitPayloadChange()
  }

  const handleQuestionnaireSelection = (questionnaireUuids: string[]) => {
    selectedQuestionnaireUuids.value = questionnaireUuids
    emitPayloadChange()
  }

  const handlePdfFormSelection = (pdfFormUuids: string[]) => {
    selectedPdfFormUuids.value = pdfFormUuids
    emitPayloadChange()
  }

  // Track PDF attachment types per uuid so we preserve PDFForm vs PDFFormTemplate on re-emit.
  const pdfAttachmentTypes = ref<Record<string, EmailAttachmentType>>({})

  const buildAttachments = (): EmailAttachment[] => {
    const attachments: EmailAttachment[] = selectedQuestionnaireUuids.value.map((uuid) => ({
      type: 'Questionnaire',
      uuid,
      metadata: { form_fields: [], annotation_data: [], signatures: {} }
    }))
    for (const uuid of selectedPdfFormUuids.value) {
      attachments.push({
        type: pdfAttachmentTypes.value[uuid] ?? 'PDFForm',
        uuid,
        metadata: { form_fields: [], annotation_data: [], signatures: {} }
      })
    }
    return attachments
  }

  // Emit payload changes to parent
  const emitPayloadChange = () => {
    const sourcePayload = (props.action?.payload ?? {}) as SendClientTemplateEmailPayload
    if (!sourcePayload.case_uuid) return
    const payload: SendClientTemplateEmailPayload = {
      case_uuid: sourcePayload.case_uuid,
      template_uuid: selectedTemplateUuid.value || undefined,
      subject: sourcePayload.subject ?? '',
      body: sourcePayload.body ?? '',
      attachments: buildAttachments()
    }
    emit('payloadChanged', payload)
  }

  // Initialize selections from action payload
  const initializeFromPayload = (payload: SendClientTemplateEmailPayload | undefined | null) => {
    if (!payload) return

    if (payload.template_uuid) {
      selectedTemplateUuid.value = payload.template_uuid
    }
    if (Array.isArray(payload.attachments)) {
      const questionnaireUuids: string[] = []
      const pdfUuids: string[] = []
      const pdfTypes: Record<string, EmailAttachmentType> = {}
      for (const attachment of payload.attachments) {
        if (!attachment?.uuid) continue
        if (attachment.type === 'Questionnaire') {
          questionnaireUuids.push(attachment.uuid)
        } else if (attachment.type === 'PDFForm' || attachment.type === 'PDFFormTemplate') {
          pdfUuids.push(attachment.uuid)
          pdfTypes[attachment.uuid] = attachment.type
        }
      }
      selectedQuestionnaireUuids.value = questionnaireUuids
      selectedPdfFormUuids.value = pdfUuids
      pdfAttachmentTypes.value = pdfTypes
    }
  }

  // Watch for action payload changes. The parent replaces the payload object
  // wholesale, so reference equality is enough — no deep watch.
  watch(
    () => props.action?.payload,
    (newPayload) => {
      initializeFromPayload(newPayload)
    }
  )

  // Watch for custom payload changes.
  watch(
    () => props.customPayload,
    (newCustomPayload) => {
      if (newCustomPayload) {
        initializeFromPayload(newCustomPayload)
      }
    }
  )

  // Initialize component
  onMounted(async () => {
    // Set initial selection from action payload or custom payload
    initializeFromPayload(props.customPayload || props.action?.payload)

    // Fetch available data
    await Promise.all([fetchTemplates(), fetchForms(), fetchQuestionnaires()])

    // Emit initial payload if any values are set
    if (
      selectedTemplateUuid.value ||
      selectedQuestionnaireUuids.value.length > 0 ||
      selectedPdfFormUuids.value.length > 0
    ) {
      emitPayloadChange()
    }
  })
</script>

<template>
  <div>
    <!-- Action Summary (committed, cancelled, reverted, or error) -->
    <div
      v-if="shouldShowSummary"
      class="space-y-3"
    >
      <div class="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        <span>{{ summaryText }}</span>
      </div>
    </div>

    <!-- Action Details (when editable) -->
    <div
      v-else
      class="space-y-3"
    >
      <div class="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        <span>{{ t('sendEmailTemplate') }}</span>

        <!-- Template Selection -->
        <template v-if="isLoadingTemplates">
          <SkeletonLoaderInlineField
            width="140px"
            height="32px"
            class="inline-block"
          />
        </template>
        <template v-else>
          <SelectField
            v-model="selectedTemplateUuid"
            variant="inline-text"
            :options="templateOptions"
            :is-filterable="false"
            :disabled="isLoadingTemplates"
            :placeholder="
              templateOptions.length === 0 ? t('noTemplatesFound') : t('selectTemplate')
            "
            name="template"
            @update:model-value="handleTemplateSelection"
          />
        </template>

        <!-- Questionnaires Section -->
        <span>
          {{
            selectedQuestionnaireUuids.length === 1
              ? t('withQuestionnaireSingular')
              : t('withQuestionnairePlural')
          }}
        </span>

        <template v-if="isLoadingQuestionnaires">
          <SkeletonLoaderInlineField
            width="160px"
            height="32px"
            class="inline-block"
          />
        </template>
        <template v-else>
          <MultiSelectField
            v-model="selectedQuestionnaireUuids"
            variant="inline-text"
            :options="questionnaireOptions"
            :is-filterable="false"
            :disabled="isLoadingQuestionnaires"
            :placeholder="
              questionnaireOptions.length === 0
                ? t('noQuestionnairesFound')
                : t('selectQuestionnaires')
            "
            name="questionnaires"
            @update:model-value="handleQuestionnaireSelection"
          />
        </template>

        <!-- PDF Forms Section -->
        <span>
          {{ selectedPdfFormUuids.length === 1 ? t('andPdfFormSingular') : t('andPdfFormPlural') }}
        </span>

        <template v-if="isLoadingForms">
          <SkeletonLoaderInlineField
            width="150px"
            height="32px"
            class="inline-block"
          />
        </template>
        <template v-else>
          <MultiSelectField
            v-model="selectedPdfFormUuids"
            variant="inline-text"
            :options="pdfFormOptions"
            :is-filterable="false"
            :disabled="isLoadingForms"
            :placeholder="pdfFormOptions.length === 0 ? t('noPdfFormsFound') : t('selectPdfForms')"
            name="pdfForms"
            @update:model-value="handlePdfFormSelection"
          />
        </template>

        <span>{{ t('toTheClient') }}</span>
      </div>
    </div>
  </div>
</template>
